import { useState } from "react";
import { Store } from "lucide-react";
import { toast } from "sonner";

import { useAgents } from "@/shared/api/agents";
import { ListCard, ListEmpty } from "@/shared/components/ListCard";
import { Note, Pane, Row, SelectInput, StatusBadge } from "@/shared/components/settings-atoms";
import { BrandButton } from "@/shared/components/ui-kit";
import { MARKETPLACE_CATEGORIES, TAGLINE_MAX } from "@/shared/types";
import { useMarketplaceListing, useSaveMarketplaceListing } from "../hooks/useMarketplaceListing";

import type { Agent, MarketplaceCategory, MarketplaceListing } from "@/shared/types";

const NO_CATEGORY = "none";
const CATEGORY_OPTIONS = [
  { id: NO_CATEGORY, label: "No category" },
  ...MARKETPLACE_CATEGORIES.map((c) => ({ id: c, label: c[0].toUpperCase() + c.slice(1) })),
];

/* Marketplace — how the agent shows in the consumer app's marketplace, where anyone can find
   it: a category for the sidebar and a tagline for its card. Only a public agent is listed, so
   any other gets the way to make it public instead of the form. Applies at once, never a draft;
   the marketplace shows a change within five minutes. */
export default function MarketplaceSection({ agent }: { agent: Agent }) {
  const agents = useAgents();
  const row = agents.data?.find((found) => found.id === agent.id);
  const isPublic = row?.visibility === "public";
  const listing = useMarketplaceListing(agent.id, isPublic);

  return (
    <Pane
      title="Marketplace"
      desc={`How ${agent.name} shows in the marketplace, where anyone can find it and start a chat. Changes apply at once and show there within five minutes.`}
    >
      <div className="mt-6 space-y-6">
        {!row ? null : !isPublic ? (
          <ListCard>
            <ListEmpty
              icon={Store}
              title="Not in the marketplace"
              hint={`Only public agents are listed. Make ${agent.name} public in Settings › Policies, under Visibility.`}
            />
          </ListCard>
        ) : listing.data ? (
          <ListingForm key={listing.data.updated_at ?? "new"} listing={listing.data} />
        ) : null}
      </div>
    </Pane>
  );
}

function ListingForm({ listing }: { listing: MarketplaceListing }) {
  const save = useSaveMarketplaceListing(listing.agent_id);
  // the words being typed; a save gives the form a new key, so it starts from the saved ones
  const [tagline, setTagline] = useState(listing.tagline ?? "");
  const send = (category: MarketplaceCategory | null, words: string, said: string) =>
    save.mutate({ category, tagline: words.trim() || null }, { onSuccess: () => toast.success(said) });

  return (
    <>
      <ListCard label="Listing" action={<ListingStatus listing={listing} />}>
        <Row className="px-4" title="Category" desc="Where it sits in the marketplace's sidebar. Without one it shows only under All and in search.">
          <SelectInput
            className="w-[320px] max-w-full"
            value={listing.category ?? NO_CATEGORY}
            options={CATEGORY_OPTIONS}
            disabled={save.isPending}
            onChange={(value) => {
              const category = value === NO_CATEGORY ? null : (value as MarketplaceCategory);
              send(category, listing.tagline ?? "", category ? "Category saved" : "Category removed");
            }}
          />
        </Row>
        <Row className="px-4" title="Tagline" desc="One line under its name on its card: what it does for someone.">
          <div className="flex items-center gap-2 w-[320px] max-w-full">
            <input
              value={tagline}
              maxLength={TAGLINE_MAX}
              onChange={(e) => setTagline(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send(listing.category, tagline, "Tagline saved")}
              placeholder="e.g. Plans your trips end to end"
              aria-label="Tagline"
              className="flex-1 min-w-0 h-8 px-2.5 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]"
            />
            <BrandButton
              onClick={() => send(listing.category, tagline, "Tagline saved")}
              disabled={save.isPending || tagline.trim() === (listing.tagline ?? "")}
              className="disabled:opacity-40 disabled:pointer-events-none"
            >
              Save
            </BrandButton>
          </div>
        </Row>
      </ListCard>
      <Note>
        Its card also shows its channels, how many chats it had this week and what a message
        costs on average, from its people's chats.
      </Note>
    </>
  );
}

function ListingStatus({ listing }: { listing: MarketplaceListing }) {
  if (listing.hidden) return <span title="Definable took it out of the marketplace. Its link and channels still work."><StatusBadge tone="danger">Hidden by Definable</StatusBadge></span>;
  if (listing.featured) return <StatusBadge tone="success">Listed · Featured</StatusBadge>;
  return <StatusBadge tone="success">Listed</StatusBadge>;
}
